
import { api, uploadWithProgress, type UploadProgress } from './client';
import type {
  ActivityFilterOptions,
  ActivityItem,
  AdminUser,
  AdminUserCreateRequest,
  AdminUserCredentials,
  AdminUserUpdateRequest,
  Artifact,
  ArtifactUrlResponse,
  Character,
  CharacterCategory,
  CharacterCreateRequest,
  CharacterJob,
  CharacterUpdateRequest,
  CutExtractRequest,
  CutRun,
  CutRunRequest,
  CutSource,
  EnrichRequest,
  HealthResponse,
  ISODateTime,
  Id,
  Insert,
  InsertMediaTypeFilter,
  InsertSearchRequest,
  InsertSearchResponse,
  InsertSortKey,
  InsertUploadRequest,
  LibraryFacets,
  LoginRequest,
  MediaLink,
  OkResponse,
  Page,
  SecretItem,
  SecretsSaved,
  SecretsUpdateRequest,
  PlacementProposalRequest,
  PlacementVideoRun,
  PlacementVideoRunRequest,
  PlacementVideoSource,
  Prompt,
  PromptCreateRequest,
  PromptUpdateRequest,
  SessionResponse,
  TagsMode,
  ToolTask,
  ToolTaskKind,
  TranslateRun,
  TranslateRunRequest,
  SubtitleBatch,
  SubtitleBatchRequest,
  SubtitleLayoutRequest,
  SubtitlePreset,
  SubtitleSettings,
  VideoGenServiceStatus,
  VideoGenTab,
  VideoGeneration,
  VideoGenerationPage,
  TtsRun,
  TtsSynthesizeRequest,
  TtsToolResponse,
  DocxTextResult,
  Font,
  FontUpdateRequest,
  FontUploadResult,
  UploadAccepted,
  Voice,
  VoiceCatalogEntry,
  VoiceCatalogItem,
  VoiceCatalogStats,
  VoiceCatalogSyncRequest,
  VoiceLibraryParams,
  SharedVoiceAddRequest,
  SharedVoiceAddResult,
  SharedVoiceSearchResult,
  VoiceCreateRequest,
  VoiceImportRequest,
  VoiceImportSpec,
  VoiceStatus,
  VoiceUpdateRequest,
} from '../types/api';

export const authApi = {
  me: () => api.get<SessionResponse>('/auth/me', { skipAuthRedirect: true }),

  login: (payload: LoginRequest) =>
    api.post<SessionResponse>('/auth/login', { json: payload, skipAuthRedirect: true }),

  logout: () => api.post<OkResponse>('/auth/logout'),
};

export const healthApi = {
  status: () => api.get<HealthResponse>('/health', { acceptStatuses: [503] }),
  live: () => api.get<OkResponse>('/health/live'),
};

export const adminApi = {
  listUsers: () => api.get<AdminUser[]>('/admin/users'),

  createUser: (payload: AdminUserCreateRequest) =>
    api.post<AdminUserCredentials>('/admin/users', { json: payload }),

  updateUser: (userId: Id, payload: AdminUserUpdateRequest) =>
    api.patch<AdminUser>(`/admin/users/${userId}`, { json: payload }),

  resetPassword: (userId: Id) =>
    api.post<AdminUserCredentials>(`/admin/users/${userId}/reset-password`),

  teams: () => api.get<string[]>('/admin/teams'),

  activity: (params: ActivityParams = {}) =>
    api.get<Page<ActivityItem>>('/admin/activity', {
      query: {
        team: params.team || undefined,
        user: params.user || undefined,
        action: params.action || undefined,
        date_from: params.date_from || undefined,
        date_to: params.date_to || undefined,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    }),

  activityFilters: () => api.get<ActivityFilterOptions>('/admin/activity/filters'),
};

export interface ActivityParams {
  team?: string;
  user?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export const settingsApi = {
  /** Только админ. Значения замаскированы, целиком не приходят. */
  secrets: () => api.get<SecretItem[]>('/settings/secrets'),

  updateSecrets: (payload: SecretsUpdateRequest) =>
    api.put<SecretsSaved>('/settings/secrets', { json: payload }),
};

export const mediaApi = {
  jobArtifacts: (jobId: Id) => api.get<Artifact[]>(`/media/jobs/${jobId}/artifacts`),

  artifactUrl: (artifactId: Id) =>
    api.get<ArtifactUrlResponse>(`/media/artifacts/${artifactId}/url`),
};

const TTS_SYNTHESIS_TIMEOUT_MS = 330_000;

const BULK_UPLOAD_TIMEOUT_MS = 30 * 60_000;

const SINGLE_UPLOAD_TIMEOUT_MS = 3 * 60_000;

function formDataOf(fields: Record<string, string | number | boolean | File | undefined | null>) {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) continue;
    form.append(key, value instanceof File ? value : String(value));
  }
  return form;
}

export const tasksApi = {
  list: (kind?: ToolTaskKind) => api.get<ToolTask[]>('/tools/tasks', { query: { kind } }),

  get: (taskId: Id) => api.get<ToolTask>(`/tools/tasks/${taskId}`),
};

export interface TtsRunsParams {
  mine_only?: boolean;
  author?: string;
  limit?: number;
  offset?: number;
}

export const ttsApi = {
  synthesize: (payload: TtsSynthesizeRequest) =>
    api.post<TtsToolResponse>('/tools/tts', {
      json: payload,
      timeoutMs: TTS_SYNTHESIS_TIMEOUT_MS,
    }),

  docxText: (file: File) =>
    api.post<DocxTextResult>('/tools/tts/docx-text', { formData: formDataOf({ file }) }),

  providerVoices: () => api.get<VoiceCatalogItem[]>('/tools/tts/voices'),

  runs: (params: TtsRunsParams = {}) =>
    api.get<Page<TtsRun>>('/tools/tts/runs', {
      query: {
        mine_only: params.mine_only,
        author: params.author || undefined,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    }),

  run: (runId: Id) => api.get<TtsToolResponse>(`/tools/tts/runs/${runId}`),
};

export interface InsertListParams {
  q?: string;
  language?: string;
  tags?: string[];
  tags_mode?: TagsMode;
  mood?: string[];
  geo?: string[];
  source?: string[];
  media_type?: InsertMediaTypeFilter;
  duration_min_ms?: number;
  duration_max_ms?: number;
  min_width?: number;
  min_height?: number;
  has_tags?: boolean;
  has_embedding?: boolean;
  created_from?: ISODateTime;
  created_to?: ISODateTime;
  sort?: InsertSortKey;
  with_media?: boolean;
  limit?: number;
  offset?: number;
}

export const libraryApi = {
  listInserts: (params: InsertListParams = {}) => {
    const q = params.q?.trim();
    const sort = params.sort === 'relevance' && !q ? '-created_at' : params.sort;
    return api.get<Page<Insert>>('/library/inserts', {
      query: {
        q,
        language: params.language,
        tags: params.tags,
        tags_mode: params.tags_mode,
        mood: params.mood,
        geo: params.geo,
        source: params.source,
        media_type: params.media_type,
        duration_min_ms: params.duration_min_ms,
        duration_max_ms: params.duration_max_ms,
        min_width: params.min_width,
        min_height: params.min_height,
        has_tags: params.has_tags,
        has_embedding: params.has_embedding,
        created_from: params.created_from,
        created_to: params.created_to,
        sort,
        with_media: params.with_media,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    });
  },

  searchInserts: (payload: InsertSearchRequest) =>
    api.post<InsertSearchResponse>('/library/inserts/search', { json: payload }),

  facets: (limit = 50) => api.get<LibraryFacets>('/library/facets', { query: { limit } }),

  get: (itemId: Id) => api.get<Insert>(`/library/inserts/${itemId}`),

  remove: (itemId: Id) => api.delete<OkResponse>(`/library/inserts/${itemId}`),

  downloadUrl: (itemId: Id) => `/api/library/inserts/${itemId}/download`,

  upload: (payload: InsertUploadRequest) =>
    api.post<UploadAccepted>('/library/inserts/upload', {
      formData: (() => {
        const form = new FormData();
        for (const file of payload.files) form.append('files', file);
        if (payload.tags) form.append('tags', payload.tags);
        if (payload.mood) form.append('mood', payload.mood);
        if (payload.geo) form.append('geo', payload.geo);
        if (payload.source) form.append('source', payload.source);
        return form;
      })(),
      timeoutMs: BULK_UPLOAD_TIMEOUT_MS,
    }),

  enrich: (payload: EnrichRequest = {}) =>
    api.post<ToolTask>('/library/inserts/enrich', { json: payload }),
};

export interface TranslateRunsParams extends PageParams {
  author?: string;
}

export const translateApi = {
  create: (payload: TranslateRunRequest) =>
    api.post<TranslateRun>('/tools/translate/runs', {
      formData: formDataOf({
        file: payload.file,
        target_language: payload.target_language,
        two_words: payload.two_words,
      }),
      timeoutMs: BULK_UPLOAD_TIMEOUT_MS,
    }),

  list: (params: TranslateRunsParams = {}) =>
    api.get<Page<TranslateRun>>('/tools/translate/runs', {
      query: {
        author: params.author || undefined,
        limit: params.limit ?? 20,
        offset: params.offset ?? 0,
      },
    }),

  get: (runId: Id) => api.get<TranslateRun>(`/tools/translate/runs/${runId}`),
};

export interface SubtitleBatchesParams extends PageParams {
  author?: string;
}

export const subtitlesApi = {
  presets: () => api.get<SubtitlePreset[]>('/tools/subtitles/presets'),

  settings: () => api.get<SubtitleSettings>('/tools/subtitles/settings'),

  fontUrl: (file: string) => `/api/tools/subtitles/fonts/${encodeURIComponent(file)}`,

  create: (payload: SubtitleBatchRequest, onProgress?: (progress: UploadProgress) => void) => {
    const form = new FormData();
    for (const file of payload.files) form.append('files', file);
    form.append('preset', payload.preset);
    form.append('shared_track', String(payload.shared_track));
    form.append('size', String(payload.size));
    return uploadWithProgress<SubtitleBatch>('/api/tools/subtitles/batches', form, {
      onProgress,
      timeoutMs: BULK_UPLOAD_TIMEOUT_MS,
    });
  },

  list: (params: SubtitleBatchesParams = {}) =>
    api.get<Page<SubtitleBatch>>('/tools/subtitles/batches', {
      query: {
        author: params.author || undefined,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    }),

  get: (batchId: Id) => api.get<SubtitleBatch>(`/tools/subtitles/batches/${batchId}`),

  restyle: (batchId: Id, preset: string) =>
    api.post<SubtitleBatch>(`/tools/subtitles/batches/${batchId}/restyle`, {
      json: { preset },
    }),

  setItemLayout: (batchId: Id, index: number, layout: SubtitleLayoutRequest) =>
    api.post<SubtitleBatch>(`/tools/subtitles/batches/${batchId}/items/${index}/layout`, {
      json: layout,
    }),

  setBatchLayout: (batchId: Id, layout: SubtitleLayoutRequest) =>
    api.post<SubtitleBatch>(`/tools/subtitles/batches/${batchId}/layout`, { json: layout }),
};

export interface PromptsListParams {
  q?: string;
  limit?: number;
  offset?: number;
}

export const promptsApi = {
  list: (params: PromptsListParams = {}) =>
    api.get<Page<Prompt>>('/prompts', {
      query: { q: params.q, limit: params.limit ?? 200, offset: params.offset ?? 0 },
    }),

  get: (promptId: Id) => api.get<Prompt>(`/prompts/${promptId}`),

  create: (payload: PromptCreateRequest) => api.post<Prompt>('/prompts', { json: payload }),

  update: (promptId: Id, payload: PromptUpdateRequest) =>
    api.patch<Prompt>(`/prompts/${promptId}`, { json: payload }),

  remove: (promptId: Id) => api.delete<OkResponse>(`/prompts/${promptId}`),
};

export interface FontsListParams {
  teams?: string[];
}

export const fontsApi = {
  list: (params: FontsListParams = {}) =>
    api.get<Page<Font>>('/fonts', { query: { team: params.teams ?? [], limit: 200, offset: 0 } }),

  teams: () => api.get<string[]>('/fonts/teams'),

  upload: (files: File[], team: string) => {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    form.append('team', team);
    return api.post<FontUploadResult>('/fonts', { formData: form });
  },

  update: (fontId: Id, payload: FontUpdateRequest) =>
    api.patch<Font>(`/fonts/${fontId}`, { json: payload }),

  remove: (fontId: Id) => api.delete<OkResponse>(`/fonts/${fontId}`),

  fileUrl: (fontId: Id, download = false) =>
    download ? `/api/fonts/${fontId}/file?download=1` : `/api/fonts/${fontId}/file`,
};

export interface CharactersListParams {
  category?: CharacterCategory;
  q?: string;
  language?: string;
  is_active?: boolean;
  has_voice?: boolean;
  ready?: boolean;
  limit?: number;
  offset?: number;
}

export interface PageParams {
  limit?: number;
  offset?: number;
}

export const charactersApi = {
  list: (params: CharactersListParams = {}) =>
    api.get<Page<Character>>('/characters', {
      query: {
        q: params.q,
        language: params.language,
        category: params.category,
        is_active: params.is_active,
        has_voice: params.has_voice,
        ready: params.ready,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    }),

  get: (characterId: Id) => api.get<Character>(`/characters/${characterId}`),

  create: (payload: CharacterCreateRequest) => api.post<Character>('/characters', { json: payload }),

  update: (characterId: Id, payload: CharacterUpdateRequest) =>
    api.patch<Character>(`/characters/${characterId}`, { json: payload }),

  remove: (characterId: Id) => api.delete<OkResponse>(`/characters/${characterId}`),

  uploadAvatar: (characterId: Id, file: File) =>
    api.post<Character>(`/characters/${characterId}/avatar`, {
      formData: formDataOf({ file }),
      timeoutMs: SINGLE_UPLOAD_TIMEOUT_MS,
    }),

  avatarUrl: (characterId: Id) => api.get<MediaLink>(`/characters/${characterId}/avatar-url`),

  jobs: (characterId: Id, params: PageParams = {}) =>
    api.get<Page<CharacterJob>>(`/characters/${characterId}/jobs`, {
      query: { limit: params.limit ?? 50, offset: params.offset ?? 0 },
    }),
};

export interface VoicesListParams {
  character_id?: Id;
  status?: VoiceStatus | VoiceStatus[];
  provider?: string;
  is_current?: boolean;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface VoiceCatalogParams {
  provider?: string;
  category?: string;
  language?: string;
  gender?: string;
  age?: string;
  q?: string;
  available?: boolean;
  is_owner?: boolean;
  refresh?: boolean;
  usable_only?: boolean;
  limit?: number;
  offset?: number;
}

export const voicesApi = {
  list: (params: VoicesListParams = {}) =>
    api.get<Page<Voice>>('/voices', {
      query: {
        character_id: params.character_id,
        status: params.status,
        provider: params.provider,
        is_current: params.is_current,
        q: params.q,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    }),

  get: (voiceId: Id) => api.get<Voice>(`/voices/${voiceId}`),

  create: (payload: VoiceCreateRequest) =>
    api.post<Voice>('/voices', {
      formData: formDataOf({
        character_id: payload.character_id,
        language: payload.language,
        name: payload.name,
        provider: payload.provider,
        external_voice_id: payload.external_voice_id,
        make_current: payload.make_current,
        sample: payload.sample,
      }),
      timeoutMs: SINGLE_UPLOAD_TIMEOUT_MS,
    }),

  update: (voiceId: Id, payload: VoiceUpdateRequest) =>
    api.patch<Voice>(`/voices/${voiceId}`, { json: payload }),

  remove: (voiceId: Id) => api.delete<OkResponse>(`/voices/${voiceId}`),

  clone: (voiceId: Id) => api.post<ToolTask>(`/voices/${voiceId}/clone`),

  sample: (voiceId: Id) => api.get<MediaLink>(`/voices/${voiceId}/sample`),

  importSpec: () => api.get<VoiceImportSpec>('/voices/import/spec'),

  import: (payload: VoiceImportRequest) => api.post<ToolTask>('/voices/import', { json: payload }),

  catalog: (params: VoiceCatalogParams = {}) =>
    api.get<Page<VoiceCatalogEntry>>('/voices/catalog', {
      query: {
        provider: params.provider,
        category: params.category,
        language: params.language,
        gender: params.gender,
        age: params.age,
        q: params.q,
        available: params.available,
        is_owner: params.is_owner,
        refresh: params.refresh,
        usable_only: params.usable_only,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      },
    }),

  catalogStats: (provider?: string) =>
    api.get<VoiceCatalogStats>('/voices/catalog/stats', { query: { provider } }),

  catalogSync: (payload: VoiceCatalogSyncRequest = {}) =>
    api.post<ToolTask>('/voices/catalog/sync', { json: payload }),

  library: (params: VoiceLibraryParams = {}) =>
    api.get<SharedVoiceSearchResult>('/voices/library', {
      query: {
        q: params.q,
        language: params.language,
        category: params.category,
        gender: params.gender,
        age: params.age,
        accent: params.accent,
        page: params.page ?? 0,
        page_size: params.page_size ?? 30,
      },
    }),

  libraryAdd: (payload: SharedVoiceAddRequest) =>
    api.post<SharedVoiceAddResult>('/voices/library/add', { json: payload }),
};

export const placementApi = {
  createProposal: (payload: PlacementProposalRequest) =>
    api.post<ToolTask>('/tools/placement/proposals', { json: payload }),

  get: (proposalId: Id) =>
    api.get<Record<string, unknown>>(`/tools/placement/proposals/${proposalId}`),

  editListUrl: (proposalId: Id) =>
    `/api/tools/placement/proposals/${proposalId}/edit-list.csv`,

  exportUrl: (proposalId: Id) => `/api/tools/placement/proposals/${proposalId}/export.zip`,

  uploadVideo: (file: File, onProgress?: (progress: UploadProgress) => void) => {
    const form = new FormData();
    form.append('file', file);
    return uploadWithProgress<PlacementVideoSource>('/api/tools/placement/videos', form, {
      onProgress,
    });
  },

  audioTimings: (
    file: File,
    script: string,
    language: string,
    onProgress?: (progress: UploadProgress) => void,
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('script', script);
    form.append('language', language);
    return uploadWithProgress<ToolTask>('/api/tools/placement/audio-timings', form, { onProgress });
  },

  createVideoRun: (payload: PlacementVideoRunRequest) =>
    api.post<PlacementVideoRun>('/tools/placement/video-runs', { json: payload }),

  videoRun: (runId: Id) => api.get<PlacementVideoRun>(`/tools/placement/video-runs/${runId}`),
};

export const cutApi = {
  uploadSource: (file: File, onProgress?: (progress: UploadProgress) => void) => {
    const form = new FormData();
    form.append('file', file);
    return uploadWithProgress<CutSource>('/api/tools/cut/sources', form, { onProgress });
  },

  analyze: (payload: CutRunRequest) => api.post<CutRun>('/tools/cut/runs', { json: payload }),

  run: (runId: Id) => api.get<CutRun>(`/tools/cut/runs/${runId}`),

  extract: (runId: Id, payload: CutExtractRequest) =>
    api.post<ToolTask>(`/tools/cut/runs/${runId}/extract`, { json: payload }),
};

export const videoGenApi = {
  status: () => api.get<VideoGenServiceStatus>('/tools/video-gen/status'),

  create: (
    tab: VideoGenTab,
    prompt: string,
    file: File,
    endFile: File | null,
    onProgress?: (progress: UploadProgress) => void,
  ) => {
    const form = new FormData();
    form.append('tab', tab);
    form.append('prompt', prompt);
    form.append('file', file);
    if (endFile) form.append('end_file', endFile);
    return uploadWithProgress<VideoGeneration>('/api/tools/video-gen/generations', form, {
      onProgress,
    });
  },

  retry: (generationId: Id) =>
    api.post<VideoGeneration>(`/tools/video-gen/generations/${generationId}/retry`),

  list: (params: PageParams = {}) =>
    api.get<VideoGenerationPage>('/tools/video-gen/generations', {
      query: { limit: params.limit ?? 50, offset: params.offset ?? 0 },
    }),

  get: (generationId: Id) =>
    api.get<VideoGeneration>(`/tools/video-gen/generations/${generationId}`),
};
